import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, BarChart, Bar } from "recharts";
import { CheckCircle2, XCircle, Rss, Newspaper, AlertTriangle, Pause, Play, Webhook, Send } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

function MetricCard({ label, value, icon: Icon, tone = "default", testid }) {
  const toneCls = {
    default: "text-white",
    success: "text-emerald-400",
    error: "text-red-400",
    warning: "text-amber-400",
  }[tone];
  return (
    <div data-testid={testid} className="border border-zinc-800 bg-[#0A0A0A] p-5 rounded-sm">
      <div className="flex items-center justify-between">
        <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
        {Icon && <Icon className="h-4 w-4 text-zinc-500" />}
      </div>
      <div className={`mt-3 font-mono font-semibold tracking-tight text-3xl md:text-4xl ${toneCls}`}>{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const qc = useQueryClient();
  const [range, setRange] = useState("24h"); // "24h" | "7d"
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard-stats", range],
    queryFn: async () => (await api.get("/dashboard/stats", { params: { range } })).data,
    refetchInterval: 15000,
  });

  const { data: sysStatus } = useQuery({
    queryKey: ["system-status"],
    queryFn: async () => (await api.get("/system/status")).data,
    refetchInterval: 10000,
  });

  const { data: heartbeat } = useQuery({
    queryKey: ["scheduler-heartbeat"],
    queryFn: async () => (await api.get("/scheduler/health")).data,
    refetchInterval: 10000,
  });

  const togglePause = useMutation({
    mutationFn: async (paused) => (await api.put("/system/status", { paused })).data,
    onSuccess: (data) => {
      toast.success(data.paused ? "System paused" : "System active");
      qc.invalidateQueries({ queryKey: ["system-status"] });
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  const { data: queueHistory } = useQuery({
    queryKey: ["webhook-queue-history"],
    queryFn: async () => (await api.get("/webhook/queue-history?hours=24")).data,
    refetchInterval: 60000,
    enabled: !!data?.webhook?.enabled,
  });

  const { data: reconciliation } = useQuery({
    queryKey: ["webhook-reconciliation"],
    queryFn: async () => (await api.get("/webhook/reconciliation?hours=24")).data,
    refetchInterval: 60000,
    enabled: !!data?.webhook?.enabled,
  });

  const paused = !!sysStatus?.paused;
  const activityRaw = data?.activity_in_range || [];
  // Compute a "nice" error-scale multiplier so the red line is visible next to OK.
  // Round to friendly steps (1, 5, 10, 20, 50, 100) and cap at 100x.
  const { activity, errorScale } = (() => {
    const maxOk = activityRaw.reduce((m, p) => Math.max(m, p.ok || 0), 0);
    const maxErr = activityRaw.reduce((m, p) => Math.max(m, p.error || 0), 0);
    let scale = 1;
    if (maxErr > 0 && maxOk / maxErr >= 3) {
      const target = maxOk / maxErr / 2; // aim ~half the OK peak
      const steps = [1, 2, 5, 10, 20, 50, 100];
      scale = steps.reduce((best, s) => (s <= target && s > best ? s : best), 1);
    }
    const data = activityRaw.map((p) => ({ ...p, error_scaled: (p.error || 0) * scale }));
    return { activity: data, errorScale: scale };
  })();
  const topErrors = data?.top_errors || [];
  const autoPausedCount = data?.auto_paused_count || 0;
  const hbAlive = heartbeat?.alive;
  const hbSecs = heartbeat?.seconds_since_last_tick;
  const hbLastTick = heartbeat?.last_tick_at;
  const wh = data?.webhook;

  const relTime = (iso) => {
    if (!iso) return "never";
    try {
      const secs = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
      if (secs < 60) return `${secs}s ago`;
      if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
      if (secs < 86400) return `${Math.floor(secs / 3600)}h ago`;
      return `${Math.floor(secs / 86400)}d ago`;
    } catch { return "—"; }
  };

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      {/* System status bar */}
      <div
        data-testid="system-status-bar"
        className={`border rounded-sm p-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3 ${paused ? "border-amber-500/50 bg-amber-500/5" : "border-zinc-800 bg-[#0A0A0A]"}`}
      >
        <div className="flex items-center gap-3">
          <div className={`h-2.5 w-2.5 rounded-full ${paused ? "bg-amber-400" : "bg-emerald-400 blink"}`} />
          <div>
            <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">System Status</div>
            <div className="font-mono text-lg tracking-tight" data-testid="system-status-label">
              {paused ? <span className="text-amber-400">PAUSED</span> : <span className="text-emerald-400">ACTIVE</span>}
            </div>
          </div>
          <div className="hidden md:block h-8 w-px bg-zinc-800 mx-2" />
          <div className="text-xs font-mono text-zinc-500 hidden md:block">
            {paused ? "Scheduler halted — no feeds being read" : "Scheduler running — feeds polled every 30s"}
          </div>
        </div>
        <Button
          data-testid="system-toggle-btn"
          onClick={() => togglePause.mutate(!paused)}
          disabled={togglePause.isPending}
          className={`rounded-sm font-mono gap-2 ${paused ? "bg-emerald-500 text-black hover:bg-emerald-400" : "bg-white text-black hover:bg-zinc-200"}`}
        >
          {paused ? <><Play className="h-4 w-4" /> RESUME SYSTEM</> : <><Pause className="h-4 w-4" /> PAUSE SYSTEM</>}
        </Button>
      </div>

      {/* Scheduler heartbeat */}
      <div
        data-testid="scheduler-heartbeat"
        className={`border rounded-sm px-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2 text-xs font-mono ${
          !heartbeat ? "border-zinc-800 bg-[#0A0A0A]" :
          paused ? "border-zinc-800 bg-[#0A0A0A]" :
          hbAlive ? "border-zinc-800 bg-[#0A0A0A]" : "border-red-500/50 bg-red-500/5"
        }`}
      >
        <div className="flex items-center gap-3">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">Scheduler heartbeat</span>
          {paused ? (
            <span className="text-amber-400" data-testid="hb-status">HALTED (system paused)</span>
          ) : hbAlive ? (
            <span className="text-emerald-400" data-testid="hb-status">HEALTHY</span>
          ) : hbLastTick ? (
            <span className="text-red-400" data-testid="hb-status">STALLED</span>
          ) : (
            <span className="text-zinc-500" data-testid="hb-status">initializing…</span>
          )}
        </div>
        <div className="flex items-center gap-4 text-zinc-400">
          <span>last_tick: <span className="text-zinc-200">{hbSecs != null ? `${hbSecs}s ago` : "—"}</span></span>
          <span>poll_interval: <span className="text-zinc-200">30s</span></span>
        </div>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.3em] text-zinc-500">Overview</div>
          <h1 className="text-2xl md:text-3xl font-mono font-semibold tracking-tight">Operational Dashboard</h1>
        </div>
        <div className="flex items-center gap-3">
          <div
            data-testid="range-toggle"
            className="inline-flex border border-zinc-800 rounded-sm overflow-hidden"
            role="tablist"
            aria-label="Metrics time range"
          >
            {[
              { key: "24h", label: "24H" },
              { key: "7d", label: "7D" },
            ].map((r) => (
              <button
                key={r.key}
                data-testid={`range-${r.key}`}
                onClick={() => setRange(r.key)}
                aria-selected={range === r.key}
                className={`px-3 py-1.5 text-[11px] font-mono tracking-widest transition-colors ${
                  range === r.key ? "bg-zinc-900 text-white" : "text-zinc-500 hover:text-white"
                }`}
              >
                {r.label}
              </button>
            ))}
          </div>
          <div className="text-[11px] font-mono text-zinc-500">auto-refresh: <span className="text-white">15s</span></div>
        </div>
      </div>

      {autoPausedCount > 0 && (
        <a
          href="/feeds"
          data-testid="auto-paused-alert"
          className="block border border-red-500/50 bg-red-500/5 rounded-sm p-4 hover:bg-red-500/10 transition-colors"
        >
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="flex-1">
              <div className="font-mono text-sm text-red-400">
                {autoPausedCount} feed{autoPausedCount === 1 ? "" : "s"} auto-paused
              </div>
              <div className="text-[11px] font-mono text-zinc-400 mt-0.5">
                Reached 20 consecutive failures. Review and resume manually in Feed Manager →
              </div>
            </div>
          </div>
        </a>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        <MetricCard label="Total" value={isLoading ? "…" : (data?.total_feeds ?? 0)} icon={Rss} testid="metric-total-feeds" />
        <MetricCard label="Active OK" value={isLoading ? "…" : (data?.ok_feeds ?? 0)} icon={CheckCircle2} tone="success" testid="metric-ok-feeds" />
        <MetricCard label="Erroring" value={isLoading ? "…" : (data?.error_feeds ?? 0)} icon={XCircle} tone="error" testid="metric-error-feeds" />
        <MetricCard label="Paused (manual)" value={isLoading ? "…" : (data?.manual_paused_count ?? 0)} tone="warning" testid="metric-manual-paused" />
        <MetricCard label="Auto-paused" value={isLoading ? "…" : (data?.auto_paused_count ?? 0)} tone="error" testid="metric-auto-paused" />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <MetricCard label={`Items downloaded ${range === "7d" ? "7d" : "24h"}`} value={isLoading ? "…" : (data?.items_in_range ?? 0)} icon={Newspaper} testid="metric-items-in-range" />
        <MetricCard label="Enabled feeds" value={isLoading ? "…" : (data?.enabled_feeds ?? 0)} icon={Rss} testid="metric-enabled" />
      </div>

      {/* Webhook widget */}
      {wh && (
        <div
          data-testid="webhook-widget"
          className={`border rounded-sm p-5 ${
            !wh.enabled ? "border-zinc-800 bg-[#0A0A0A]" :
            wh.last_error_at && (!wh.last_success_at || new Date(wh.last_error_at) > new Date(wh.last_success_at)) ? "border-red-500/50 bg-red-500/5" :
            "border-zinc-800 bg-[#0A0A0A]"
          }`}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <Webhook className="h-4 w-4 text-zinc-400" />
              <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Webhook forwarding</span>
              {wh.enabled ? (
                <span className="text-[10px] font-mono text-emerald-400 border border-emerald-500/40 px-2 py-0.5 rounded-sm uppercase tracking-widest">ON</span>
              ) : (
                <span className="text-[10px] font-mono text-zinc-500 border border-zinc-700 px-2 py-0.5 rounded-sm uppercase tracking-widest">OFF</span>
              )}
            </div>
            {wh.url && <span className="text-[10px] font-mono text-zinc-500 truncate max-w-[280px]" title={wh.url}>{wh.url}</span>}
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Delivered {range === "7d" ? "7d" : "24h"}</div>
              <div className="mt-1 font-mono font-semibold tracking-tight text-3xl text-emerald-400" data-testid="wh-delivered-in-range">{wh.delivered_in_range ?? 0}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Pending in queue</div>
              <div className={`mt-1 font-mono font-semibold tracking-tight text-3xl ${wh.pending > 0 ? "text-amber-400" : "text-white"}`} data-testid="wh-pending">{wh.pending ?? 0}</div>
            </div>
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 flex items-center gap-1"><Send className="h-3 w-3" /> Last delivery</div>
              <div className="mt-1 font-mono text-xl text-white" data-testid="wh-last-success">{relTime(wh.last_success_at)}</div>
              {wh.last_error_at && (
                <div className="mt-1 text-[10px] font-mono text-red-400 truncate" title={wh.last_error}>
                  last error: {relTime(wh.last_error_at)}
                </div>
              )}
            </div>
          </div>

          {/* Queue depth chart */}
          {wh.enabled && queueHistory?.points?.length > 1 && (
            <div className="mt-5 pt-5 border-t border-zinc-800" data-testid="webhook-queue-chart">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Queue depth · last 24h</div>
              <div className="h-[180px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={queueHistory.points} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                    <CartesianGrid stroke="#1f1f22" vertical={false} />
                    <XAxis dataKey="at" stroke="#71717A" tick={{ fontFamily: 'IBM Plex Mono', fontSize: 10 }} tickFormatter={(v) => (v || "").slice(11, 16)} />
                    <YAxis stroke="#71717A" tick={{ fontFamily: 'IBM Plex Mono', fontSize: 10 }} allowDecimals={false} />
                    <Tooltip
                      contentStyle={{ background: "#050505", border: "1px solid #27272A", borderRadius: 2, fontFamily: "IBM Plex Mono", fontSize: 12 }}
                      labelStyle={{ color: "#A1A1AA" }}
                      labelFormatter={(v) => (v || "").replace("T", " ").slice(0, 19)}
                    />
                    <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 11, color: "#A1A1AA" }} />
                    <Line type="monotone" dataKey="pending" stroke="#F59E0B" strokeWidth={1.5} dot={false} name="Pending" />
                    <Line type="monotone" dataKey="delivered_last_hour" stroke="#34C759" strokeWidth={1.5} dot={false} name="Delivered/hr" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}
          {wh.enabled && (!queueHistory?.points || queueHistory.points.length <= 1) && (
            <div className="mt-4 pt-4 border-t border-zinc-800 text-[10px] font-mono text-zinc-500">
              Queue-depth chart populates as snapshots accumulate (every 2 min).
            </div>
          )}
        </div>
      )}

      {/* Reconciliation widget — created vs delivered per hour */}
      {wh?.enabled && reconciliation && (
        <div className="border border-zinc-800 bg-[#0A0A0A] rounded-sm p-5" data-testid="reconciliation-widget">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              Delivery reconciliation · last 24h
            </div>
            {(() => {
              const t = reconciliation.totals || {};
              const gone = (t.gave_up || 0) + (t.unrecoverable || 0);
              const pct = t.created ? ((t.delivered / t.created) * 100).toFixed(2) : "—";
              return (
                <div className={`text-[10px] font-mono uppercase tracking-widest ${gone > 0 ? "text-red-400" : "text-emerald-400"}`}>
                  {gone > 0 ? "LOSS DETECTED" : "NO LOSS"}
                </div>
              );
            })()}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            {(() => {
              const t = reconciliation.totals || {};
              const items = [
                { label: "Created (Mongo)", value: t.created ?? 0, testid: "recon-created", tone: "text-white" },
                { label: "Delivered → n8n", value: t.delivered ?? 0, testid: "recon-delivered", tone: "text-emerald-400" },
                { label: "Still pending", value: t.still_pending ?? 0, testid: "recon-pending", tone: (t.still_pending || 0) > 0 ? "text-amber-400" : "text-white" },
                { label: "Gave up (lost)", value: t.gave_up ?? 0, testid: "recon-gave-up", tone: (t.gave_up || 0) > 0 ? "text-red-400" : "text-white" },
                { label: "Rejected 4xx", value: t.unrecoverable ?? 0, testid: "recon-unrecoverable", tone: (t.unrecoverable || 0) > 0 ? "text-red-400" : "text-white" },
              ];
              return items.map((m) => (
                <div key={m.label} className="border border-zinc-800 bg-[#050505] p-3 rounded-sm">
                  <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{m.label}</div>
                  <div className={`mt-1 font-mono font-semibold tracking-tight text-2xl ${m.tone}`} data-testid={m.testid}>{m.value}</div>
                </div>
              ));
            })()}
          </div>

          {/* Bars: created vs delivered per hour */}
          {reconciliation.points?.length > 1 && (
            <div className="h-[180px]" data-testid="reconciliation-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={reconciliation.points} margin={{ top: 5, right: 10, bottom: 0, left: -10 }}>
                  <CartesianGrid stroke="#1f1f22" vertical={false} />
                  <XAxis dataKey="hour" stroke="#71717A" tick={{ fontFamily: 'IBM Plex Mono', fontSize: 10 }} tickFormatter={(v) => (v || "").slice(11, 16)} />
                  <YAxis stroke="#71717A" tick={{ fontFamily: 'IBM Plex Mono', fontSize: 10 }} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ background: "#050505", border: "1px solid #27272A", borderRadius: 2, fontFamily: "IBM Plex Mono", fontSize: 12 }}
                    labelStyle={{ color: "#A1A1AA" }}
                    labelFormatter={(v) => (v || "").replace("T", " ")}
                  />
                  <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 11, color: "#A1A1AA" }} />
                  <Bar dataKey="created" fill="#A1A1AA" name="Created" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="delivered" fill="#34C759" name="Delivered" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="gave_up" fill="#FF3B30" name="Gave up" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {reconciliation.recent_gave_up?.length > 0 && (
            <div className="mt-5 pt-5 border-t border-zinc-800">
              <div className="text-[10px] font-mono uppercase tracking-widest text-red-400 mb-2">Recent gave-up items ({reconciliation.recent_gave_up.length})</div>
              <div className="space-y-1">
                {reconciliation.recent_gave_up.slice(0, 10).map((it, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs font-mono">
                    <span className="text-zinc-500 w-16 truncate" title={it.fetched_at}>{relTime(it.fetched_at)}</span>
                    <span className="text-zinc-400 w-40 truncate" title={it.feed_name}>{it.feed_name}</span>
                    <span className="text-zinc-300 flex-1 truncate" title={it.title}>{it.title}</span>
                    <span className="text-red-400 truncate max-w-[240px]" title={it.webhook_delivery_error}>{it.webhook_delivery_error}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="border border-zinc-800 bg-[#0A0A0A] rounded-sm">
        <div className="flex items-center justify-between px-5 py-4 border-b border-zinc-800">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Fetch Activity</div>
            <div className="font-mono text-sm">{range === "7d" ? "Last 7 days" : "Last 24 hours"}</div>
          </div>
          {errorScale > 1 && (
            <div
              data-testid="fetch-error-scale-hint"
              className="text-[10px] font-mono text-zinc-500 uppercase tracking-widest"
              title={`Error line is drawn at ${errorScale}× its real value so the trend is visible next to the much larger OK line.`}
            >
              ERR line scaled ×{errorScale} for visibility
            </div>
          )}
        </div>
        <div className="p-4 h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={activity} margin={{ top: 10, right: 20, bottom: 0, left: -10 }}>
              <CartesianGrid stroke="#1f1f22" strokeDasharray="0" vertical={false} />
              <XAxis
                dataKey="hour"
                stroke="#71717A"
                tick={{ fontFamily: 'IBM Plex Mono', fontSize: 10 }}
                tickFormatter={(v) => {
                  if (!v) return "";
                  // 7d buckets are date-only (YYYY-MM-DD) → show "MMM DD"
                  if (range === "7d") {
                    try {
                      const d = new Date(v + "T00:00:00Z");
                      return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
                    } catch { return v; }
                  }
                  // 24h buckets are "YYYY-MM-DD HH:00" → show "HH:00"
                  return v.slice(11, 16);
                }}
              />
              <YAxis stroke="#71717A" tick={{ fontFamily: 'IBM Plex Mono', fontSize: 10 }} allowDecimals={false} />
              <Tooltip
                contentStyle={{ background: "#050505", border: "1px solid #27272A", borderRadius: 2, fontFamily: "IBM Plex Mono", fontSize: 12 }}
                labelStyle={{ color: "#A1A1AA" }}
                formatter={(value, name, entry) => {
                  if (entry?.dataKey === "error_scaled") {
                    const real = entry?.payload?.error ?? 0;
                    return [`${real} (drawn ×${errorScale})`, "ERR"];
                  }
                  return [value, name];
                }}
              />
              <Legend wrapperStyle={{ fontFamily: "IBM Plex Mono", fontSize: 11, color: "#A1A1AA" }} />
              <Line type="monotone" dataKey="ok" stroke="#34C759" strokeWidth={1.5} dot={false} name="OK" />
              <Line type="monotone" dataKey="error_scaled" stroke="#FF3B30" strokeWidth={1.5} dot={false} name={errorScale > 1 ? `ERR ×${errorScale}` : "ERR"} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="border border-zinc-800 bg-[#0A0A0A] rounded-sm">
        <div className="flex items-center gap-2 px-5 py-4 border-b border-zinc-800">
          <AlertTriangle className="h-4 w-4 text-amber-400" />
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Top Failing Feeds</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 border-b border-zinc-800">
                <th className="text-left px-5 py-2">Name</th>
                <th className="text-left px-5 py-2">URL</th>
                <th className="text-left px-5 py-2">Error</th>
                <th className="text-right px-5 py-2">Fails</th>
              </tr>
            </thead>
            <tbody data-testid="top-errors-tbody">
              {topErrors.length === 0 && (
                <tr><td colSpan={4} className="px-5 py-6 text-center text-zinc-500 font-mono text-xs">— no failing feeds —</td></tr>
              )}
              {topErrors.map((e) => (
                <tr key={e.id} className="border-b border-zinc-900 hover:bg-zinc-900/60">
                  <td className="px-5 py-2 font-mono">{e.name}</td>
                  <td className="px-5 py-2 font-mono text-xs text-zinc-400 max-w-xs truncate">{e.url}</td>
                  <td className="px-5 py-2 font-mono text-xs text-red-400 max-w-xs truncate">{e.last_error}</td>
                  <td className="px-5 py-2 text-right"><Badge variant="outline" className="rounded-sm border-red-500/50 text-red-400 font-mono">{e.consecutive_failures}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
